import { faGears } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useParams } from 'react-router-dom';
import { client } from '~/client';
import { Site, useSites } from '~/components/FindAddSite/FindAddSite';
import { Split } from '~/components/Split';
import { CardNew } from '~/components/ui/CardNew';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { graphql } from '~/gql';
import {
  AutomationWhereType,
  ListAutomationsQuery,
  Status,
} from '~/gql/graphql';
import { useSearch } from '~/hooks/useSearch';
import { JobsNav } from '~/layouts/nav/JobsNav';

// Navigation component that shows JobsNav consistently
const AutomationsNav = () => (
  <>
    <JobsNav />
  </>
);

const SEARCH_OPTIONS = {
  keys: ['name', 'on', 'template.name', 'assignee.name'],
};

export const ListAutomationsDocument = graphql(`
  query ListAutomations {
    automations {
      id
      status
      name
      on
      whereType
      matchAttribute {
        key: name
        value
      }
      sites {
        id
        name
        image
      }
      do
      jobName
      template {
        id
        status
        name
        tags {
          id
          entityType
          category
          name
          description
          colour
        }
        notes
        includeAttributes
        jobAttributeIds
        attachments {
          id
          kind
          ext
          thumb
          url
          originalFilename
        }
        guestyCleaningStatus
        tasks {
          id
          type
          name
          description
          tags {
            nodes {
              id
              entityType
              name
              category
              description
              colour
            }
          }
          attributes {
            id
            type
            category
            name
            value
          }
          jobAttributeIds
          attribute {
            id
            attributes
          }
          purchase {
            id
            spaces
          }
          stocktake {
            id
            include
            itemStatus
            spaces
            items {
              id
              adjustment
              newOnHand
            }
          }
          config {
            selection
            spaces
            assetTypes
            allow
            shareAttributes
            shareJobHistory
            shareDeploymentHistory
          }
          assets {
            id
            name
            type
          }
        }
      }
      owners {
        id
        name
        image
      }
      assignees {
        id
        name
        image
      }
      day
      time
      conditions {
        type
        operator
        nights
      }
      repeatUntilOffset
      repeatStartDate
      repeat {
        freq
        interval
        byday
        bymonthday
      }
      maintainRepeats
    }
  }
`);

export async function loader() {
  const { data } = await client
    .query(ListAutomationsDocument, {}, { requestPolicy: 'cache-and-network' })
    .toPromise();

  return data?.automations ?? null;
}

export function Component() {
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>;
  const {
    results: automations,
    search,
    query,
  } = useSearch(data ?? undefined, SEARCH_OPTIONS);
  const [allSites] = useSites();
  const { t } = useTranslation();

  const [filtersValue, setFiltersValue] = useState(
    new URLSearchParams([['status', Status.Active]])
  );

  // Convert sites to filter options
  const siteFilterOptions = useMemo(
    () =>
      allSites
        .filter(({ status }) => status === 'Active')
        .map(({ id, name }) => ({
          value: id,
          label: name,
        })),
    [allSites]
  );

  // Sort automations by name
  const sortedAutomations = automations?.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  // Filter automations by filters
  const filteredAutomations = sortedAutomations?.filter((automation) => {
    const status = filtersValue.get('status');
    const selectedSiteIds = filtersValue.getAll('site');

    // Filter by status
    if (status && automation.status !== status) {
      return false;
    }

    // Filter by site - if sites are selected, automation must have at least one matching site
    if (selectedSiteIds.length > 0) {
      const automationSiteIds = automation.sites.map((site) => site.id);
      const hasMatchingSite = selectedSiteIds.some((siteId) =>
        automationSiteIds.includes(siteId)
      );
      if (!hasMatchingSite) {
        return false;
      }
    }

    return true;
  });

  return (
    <>
      {/* Authorization is intentionally disabled for this view while pricing is being implemented. */}
      {/* <AuthGate action='manage' subject='feat.automation'> */}
      <Split
        nav={<AutomationsNav />}
        filters={
          <FilterGroupSearch
            filters={[
              {
                type: 'select',
                name: 'status',
                label: 'Status',
                options: [
                  { label: 'Active', value: Status.Active },
                  { label: 'Inactive', value: Status.Inactive },
                ],
                multiple: false,
                searchable: false,
              },
              {
                type: 'select',
                name: 'site',
                label: t('site'),
                options: siteFilterOptions,
                multiple: true,
                searchable: true,
              },
            ]}
            value={{
              searchValue: query,
              filtersValue: filtersValue,
            }}
            onChange={({ searchValue, filtersValue }) => {
              search(searchValue);
              setFiltersValue(filtersValue);
            }}
            placement={'portal'}
          />
        }
        main={
          <div className='pb-8'>
            {filteredAutomations?.map((automation) => (
              <Item
                key={automation.id}
                automation={automation}
                allSites={allSites}
              />
            ))}
          </div>
        }
        mobileAction='Automation'
      />
      {/* <div style={{ height: 'calc(100svh - 5rem)' }}> */}

      {/* </div> */}
      {/* </AuthGate> */}
    </>
  );
}

function Item({
  automation,
  allSites,
}: {
  automation: ListAutomationsQuery['automations'][0];
  allSites: Site[];
}) {
  const { automationId } = useParams();
  const { t } = useTranslation();

  const tertiaryText = useMemo(() => {
    const jobName = automation.jobName;

    // If attribute selection (match)
    if (automation.whereType === AutomationWhereType.Match) {
      return `${jobName} for ${t('places_matching_attributes', {
        defaultValue: 'places matching selected attributes',
      })}`;
    }

    // If all places selected
    if (automation.whereType === AutomationWhereType.All) {
      return `${jobName} for All places`;
    }

    // If select mode - check if all sites are selected
    if (automation.whereType === AutomationWhereType.Select) {
      const automationSiteIds = new Set(
        automation.sites.map((site) => site.id)
      );
      const allSiteIds = new Set(allSites.map((site) => site.id));
      const allSelected =
        allSiteIds.size > 0 &&
        automationSiteIds.size === allSiteIds.size &&
        Array.from(allSiteIds).every((id) => automationSiteIds.has(id));

      if (allSelected) {
        return `${jobName} for All places`;
      }
    }

    // Fallback to original behavior
    return `${jobName} for ${automation.sites
      .map(({ name }) => name)
      .join(', ')}`;
  }, [automation, allSites]);

  return (
    <CardNew
      linkTo={automation.id}
      titleText={automation.name}
      secondaryText={<span className='capitalize'>{t(automation.on)}</span>}
      tertiaryText={tertiaryText}
      // status={automation.status}
      image={
        <div className='flex h-full w-full items-center justify-center bg-shade-10'>
          <FontAwesomeIcon
            className='text-2xl text-shade-40'
            icon={faGears as IconProp}
          />
        </div>
      }
      isSelected={automationId === automation.id}
      status={automation.status}
      showImage
    />
  );
}
